import {createElement, FC, ReactElement} from 'react';
import {Box, Skeleton, Stack, Typography} from '@mui/material';
import GenericHeader from '../generic-header/generic-header';
import {ShiftCard} from '../../screens/private/office/professional-caregiver/professional-caregivers/shifts/shift-card';
import {AffiliatedUser} from '../../backend/responses/affiliated-user';
import {ScreenPaths} from '../../enums';
import {useObserveViewport} from '../../hooks';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {EntityShift} from '../../backend/interfaces/entity-shift';
import {EntitySocialResponse} from '../../backend/interfaces/entity-social-response';
import {useParams} from 'react-router-dom';

interface IndexViewScreenProps {
  /* page specific props */
  shifts: EntityShift[] | undefined | null;
  canWriteShifts: boolean;
  collaborators: AffiliatedUser[] | undefined | null;
  entitySocialResponses?: EntitySocialResponse[] | null;
  pageHeader?: {
    screenTitle?: string;
    searchLabel?: string;
    rightButton?: {
      label: string;
      toLink: string;
      endIcon: ReactElement;
    };
  };

  /* page query props */
  searchOptions: {
    searchString?: boolean;
    toggleDisplay?: boolean;
    filters?: any[];
  };

  /* page UI related props */
  rightColumn: FC<{resultsLength: number}>;
  emptyStateMessage: {title: string; subtitle: string};
  renderItem: {
    list?: {
      element: (data: any) => ReactElement;
      skeleton: () => ReactElement;
    };
    grid?: {
      element: (data: any) => ReactElement;
      skeleton: () => ReactElement;
      gridItemWidth: number;
    };
  };
  updateCallback?: () => void;
}

export const ShiftsIndexViewScreen = ({
  shifts,
  canWriteShifts,
  collaborators,
  entitySocialResponses,
  rightColumn,
  emptyStateMessage,
  updateCallback,
}: IndexViewScreenProps) => {
  const {entityId}: {entityId: string} = useParams();
  const {t} = useTranslation();
  const {isRunningMobile} = useObserveViewport();

  return (
    <Stack rowGap={2}>
      <GenericHeader
        titleCrumbs={
          isRunningMobile
            ? [{label: t(translations.shifts_page.shifts), link: '#'}]
            : [
                {
                  label: t(translations.shifts_page.team),
                  link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}`,
                },
                {label: t(translations.shifts_page.shifts), link: '#'},
              ]
        }
        isLoading={shifts === undefined || collaborators === undefined}
        leftColumn={
          <Stack
            gap={2}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              padding: 0,
              width: '100%',
            }}
          ></Stack>
        }
        rightColumn={
          <>
            {createElement<{resultsLength: number}>(rightColumn, {
              resultsLength: !shifts ? 0 : shifts.length,
            })}
          </>
        }
      />

      <Stack component="section" className="index-view-screen-results-wrapper">
        {shifts && shifts.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: 'calc(100vh - 300px)',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Roboto',
                color: '#464646',
                fontSize: '20px',
                fontWeight: 600,
              }}
            >
              {emptyStateMessage.title}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Roboto',
                color: '#464646',
                fontSize: '16px',
                fontWeight: 400,
              }}
            >
              {emptyStateMessage.subtitle}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridGap: '16px',
              justifyContent: 'space-between',
              gridTemplateColumns: `repeat(auto-fill, 329px)`,
            }}
          >
            {shifts &&
              shifts.map((shift) =>
                collaborators === undefined || collaborators === null ? (
                  <Skeleton
                    key={`shift-skeleton-${shift.id}`}
                    animation={'wave'}
                    variant={'rounded'}
                    sx={{height: '192px', width: '329px'}}
                  />
                ) : (
                  <ShiftCard
                    key={`shift-card-${shift.id}`}
                    shift={shift}
                    actionsEnabled={canWriteShifts}
                    allCollaborators={collaborators!}
                    updateCallback={updateCallback}
                  />
                )
              )}
          </Box>
        )}
      </Stack>
    </Stack>
  );
};
